import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

import { ModalKeyboardDirective } from '../modal-keyboard/modal-keyboard.directive';

@Component({
  selector: 'lib-page-action-modal',
  standalone: true,
  imports: [ModalKeyboardDirective],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (isOpen) {
      <div class="page-action-modal__backdrop" (click)="close.emit()">
        <section
          class="page-action-modal"
          [style.--page-action-modal-width]="width"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          [libModalKeyboardCanCancel]="canClose"
          [libModalKeyboardCanSubmit]="canSubmit"
          [libModalKeyboardAutofocus]="autofocus"
          (libModalKeyboardCancel)="close.emit()"
          (libModalKeyboardSubmit)="submit.emit()"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="title"
        >
          <header class="page-action-modal__head">
            <div class="page-action-modal__title-wrap">
              <h3 class="page-action-modal__title">{{ title }}</h3>
              @if (subtitle) {
                <p class="page-action-modal__subtitle">{{ subtitle }}</p>
              }
            </div>

            @if (showCloseButton && canClose) {
              <button
                type="button"
                class="page-action-modal__close"
                (click)="close.emit()"
                [attr.aria-label]="closeAriaLabel"
              >
                ×
              </button>
            }
          </header>

          <ng-content />
        </section>
      </div>
    }
  `,
  styles: [`
    .page-action-modal__backdrop {
      position: fixed;
      inset: 0;
      z-index: 1100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .page-action-modal {
      width: min(var(--page-action-modal-width, 560px), 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border: 1px solid rgba(226, 232, 240, 0.94);
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
      padding: 20px;
      box-sizing: border-box;
      display: grid;
      gap: 16px;
    }

    .page-action-modal__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    .page-action-modal__title-wrap {
      min-width: 0;
    }

    .page-action-modal__title {
      margin: 0;
      color: #111827;
      font-size: 22px;
      line-height: 1.18;
      font-weight: 800;
    }

    .page-action-modal__subtitle {
      margin: 6px 0 0;
      color: #64748b;
      font-size: 14px;
      line-height: 1.45;
    }

    .page-action-modal__close {
      width: 42px;
      height: 42px;
      flex: 0 0 auto;
      border: 1px solid #cbd5e1;
      border-radius: 999px;
      background: #ffffff;
      color: #475569;
      cursor: pointer;
      font-size: 26px;
      line-height: 1;
    }

    .page-action-modal :is(button, input, select, textarea):focus-visible,
    .page-action-modal :is(button, input, select, textarea):focus {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }

    .page-action-modal :is(input, select, textarea):focus-visible,
    .page-action-modal :is(input, select, textarea):focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
      outline: none;
    }

    @media (max-width: 640px) {
      .page-action-modal__backdrop {
        align-items: flex-end;
        padding: 0;
      }

      .page-action-modal {
        width: 100%;
        max-height: 92vh;
        border-radius: 18px 18px 0 0;
        padding: 16px;
      }
    }
  `],
})
export class PageActionModalComponent {
  @Input() public isOpen = false;
  @Input() public title = 'Actions';
  @Input() public subtitle = '';
  @Input() public width = '560px';
  @Input() public canClose = true;
  @Input() public canSubmit = false;
  @Input() public autofocus = true;
  @Input() public showCloseButton = true;
  @Input() public closeAriaLabel = 'Close modal';

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly submit = new EventEmitter<void>();
}
